import React, { useCallback, useMemo } from 'react';
import {
  QBox,
  QButton,
  QFlex,
  QFormControl,
  QInput,
  QReorderableCard,
  QReorderableCardStack,
  QStack,
  useToastProvider,
} from '@qualio/ui-components';
import { TemplateDetail, UpdateSection } from '../../types';
import { Editor } from '../Editor/Editor';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export const InlineEditSectionsForm: React.FC<{ data: TemplateDetail }> = ({ data }) => {
  const { t } = useTranslation();
  const { showToast } = useToastProvider();

  const {
    control,
    setError,
    getValues,
    formState: { errors },
  } = useFormContext<UpdateSection>();

  const {
    fields: sections,
    swap,
    remove,
    append,
  } = useFieldArray({
    control: control,
    name: 'sections',
  });

  const onChange = useCallback(
    (ids: string[]) => {
      if (sections.length === ids.length) {
        for (const [index, section] of sections.entries()) {
          const correctIndex = ids.indexOf(`${index}-${section.id}`);
          if (correctIndex !== index) {
            swap(index, correctIndex);
            break;
          }
        }
      } else {
        for (const [index, section] of sections.entries()) {
          if (ids.length === 0) {
            showToast({
              description: t('template.sections.errors.atLeastOne'),
              status: 'error',
              title: t('common.toast.error'),
              'data-cy': 'toast-inline-edit-sections-at-least-one',
            });
            break;
          }
          if (!ids.includes(`${index}-${section.id}`)) {
            remove(index);
            break;
          }
        }
      }
    },
    [swap, remove, sections, showToast, t],
  );

  const validateTitleUniqueness = (e: React.FocusEvent<HTMLInputElement, Element>) => {
    const target = e.target;
    const inputIndex = Number(target.name.split('.')[1]);
    const allSections = getValues('sections');
    allSections.forEach((section, index) => {
      if (inputIndex !== index && section.title.toLowerCase() === target.value.toLowerCase()) {
        setError(`sections.${inputIndex}.title`, {
          type: 'manual',
          message: t('template.sections.errors.uniqueTitles'),
        });
      }
    });
  };

  const addNewSection = useCallback(() => append({ id: undefined, title: '', content: '' }), [append]);

  useMemo(() => {
    if (sections.length === 0) {
      addNewSection();
    }
  }, [addNewSection, sections]);

  return (
    <QBox width="100%" mt={-6}>
      <QReorderableCardStack onChange={onChange}>
        {sections.map((section, index) => (
          <QReorderableCard key={`section-${section.id}`} id={`${index}-${section.id}`}>
            <QStack>
              <QFlex flexDirection={'column'} alignItems={'flex-start'} gap="24px">
                <QBox width="100%" maxWidth={'560px'}>
                  <QFormControl
                    label={t('template.sections.sectionTitle')}
                    error={errors.sections?.[index]?.title?.message}
                    isInvalid={!!errors.sections?.[index]?.title?.message}
                    isRequired
                  >
                    <Controller
                      control={control}
                      name={`sections.${index}.title`}
                      render={({ field: { ref: _ref, ...field } }) => (
                        <QInput
                          {...field}
                          onBlur={(e) => validateTitleUniqueness(e)}
                          data-cy={`index-${index}`}
                          value={field.value}
                        />
                      )}
                    />
                  </QFormControl>
                </QBox>
                <QFormControl label={t('template.sections.defaultContent')}>
                  <Controller
                    control={control}
                    name={`sections.${index}.content`}
                    render={({ field: { ref: _ref, onChange, ...field } }) => (
                      <Editor onChange={(data: string) => onChange(data)} index={index} value={section.content} />
                    )}
                  />
                </QFormControl>
              </QFlex>
            </QStack>
          </QReorderableCard>
        ))}
        <QButton variant="outline" onClick={addNewSection} data-cy="add-section-button" leftIcon="Plus">
          {t('template.sections.addSection')}
        </QButton>
      </QReorderableCardStack>
    </QBox>
  );
};
